import { faChevronDown } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from '@tanstack/react-router';
import { Fragment, useId } from 'react';
import { cn } from '@/lib/utils/cn';
import {
  childBase,
  childInactive,
  childSelected,
  iconSize,
  rowBase,
  rowIcon,
  rowInactive,
  rowInSection,
} from './styles';
import type { NavChild, NavGroup } from './types';

/**
 * A first-level group and, when unfolded, its second level directly beneath it
 * — the whole point of the expanded column: one column, both levels, nothing
 * behind a hover.
 *
 * Returns a fragment rather than a wrapper, so the row and its children are
 * both direct children of the column's stack and take the stack's gap. Wrapping
 * them would put the group's own box between them and the rhythm.
 */
export function ColumnGroup({
  group,
  items,
  open,
  activeKey,
  onToggle,
  onNavigate,
}: {
  group: NavGroup;
  /** This group's children, already filtered by each child's `visible()`. */
  items: NavChild[];
  open: boolean;
  /** The child matching the current route, if this group owns it. */
  activeKey: string | undefined;
  onToggle: () => void;
  onNavigate?: (() => void) | undefined;
}) {
  const listId = useId();
  // While the group is unfolded the selected child below carries the "you are
  // here" state, so the group row would be a second, competing highlight.
  const inSection = activeKey !== undefined && !open;

  return (
    <Fragment>
      <button
        type='button'
        aria-label={group.label}
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        onClick={onToggle}
        className={cn(rowBase, inSection ? rowInSection : rowInactive)}
      >
        <span className={rowIcon}>
          <FontAwesomeIcon icon={group.icon} className={iconSize} />
        </span>
        <span className='min-w-0 flex-1 truncate'>{group.label}</span>
        <FontAwesomeIcon
          icon={faChevronDown}
          className={cn(
            'size-[13px] shrink-0 opacity-50 transition-transform duration-200',
            open && 'rotate-180',
          )}
        />
      </button>

      {open && (
        // Indented to the parent's label, with the rule at 18px — the centre of
        // the icon above it — so the second level reads as hanging off that row
        // rather than as a separate list that happens to be indented.
        <div
          id={listId}
          className="relative flex flex-col gap-0.5 pl-9 before:absolute before:left-[18px] before:top-0.5 before:bottom-1 before:w-px before:bg-sidebar-foreground/20 before:content-['']"
        >
          {items.map(child => {
            const active = child.key === activeKey;
            return (
              <Link
                key={child.key}
                to={child.href as never}
                onClick={onNavigate}
                aria-current={active ? 'page' : undefined}
                className={cn(
                  childBase,
                  active ? childSelected : childInactive,
                )}
              >
                <span className='truncate'>{child.label}</span>
              </Link>
            );
          })}
        </div>
      )}
    </Fragment>
  );
}
